import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, Building2, MessageSquare, LibraryBig, Plus, Trash2, Loader2, Search, Sparkles, CreditCard, Wand2, BarChart3, DollarSign, ChevronLeft, Save, UserCog, ShieldAlert } from "lucide-react";
import { Header } from "@/components/Header";
import { api, mediaUrl, apiError } from "@/lib/api";

export default function AdminConsole() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [tab, setTab] = useState("chat");
  const [payingTest, setPayingTest] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [showUsage, setShowUsage] = useState(false);

  const cleanupTest = async () => {
    try {
      const { data: prev } = await api.get("/admin/test-data-preview");
      if (!prev.count) return toast.info("No test accounts found to remove.");
      if (!window.confirm(`Remove ${prev.count} test account(s) and all data they created?\n\nReal customer signups, the demo, and admin accounts are kept. This cannot be undone.`)) return;
      setCleaning(true);
      const { data } = await api.post("/admin/cleanup-test-data");
      toast.success(`Removed ${data.companies_removed} test account(s) and ${data.users_removed} user(s).`);
      setSel(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Cleanup failed.");
    } finally {
      setCleaning(false);
    }
  };

  const payTest = async () => {
    setPayingTest(true);
    try {
      const { data } = await api.post("/payments/test-charge", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not start the test charge.");
      setPayingTest(false);
    }
  };

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/companies"); setCompanies(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = companies.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen">
      <Header />
      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr]">
        <aside className={`thin-scroll border-r border-[var(--line)] bg-white p-4 md:max-h-[calc(100vh-61px)] md:overflow-y-auto ${(sel || showUsage) ? "hidden md:block" : "block"}`}>
          <button data-testid="admin-design-studio-btn" onClick={() => navigate("/studio")}
            className="mb-3 flex w-full items-center justify-center gap-2 rounded-full bg-[var(--ink)] py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-px">
            <Wand2 className="h-4 w-4 text-[var(--brand)]" strokeWidth={2} /> Design Studio
          </button>
          <p className="mb-3 text-center text-[11px] text-[var(--ink-soft)]">Unlimited admin access — no trial or daily limits.</p>

          <button data-testid="cleanup-test-data-btn" onClick={cleanupTest} disabled={cleaning}
            className="mb-4 flex w-full items-center justify-center gap-1.5 rounded-full border border-[var(--line)] py-2 text-xs font-semibold text-[var(--ink-soft)] transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-60">
            {cleaning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Remove test data
          </button>
          <button data-testid="usage-dashboard-btn" onClick={() => { setShowUsage(true); setSel(null); }}
            className={`mb-4 flex w-full items-center justify-center gap-1.5 rounded-full py-2 text-xs font-semibold transition-colors ${showUsage ? "bg-[var(--ink)] text-white" : "border border-[var(--line)] hover:bg-[var(--bone)]"}`}>
            <BarChart3 className="h-3.5 w-3.5" /> Usage &amp; profit
          </button>
          <p className="overline mb-2">Companies ({companies.length})</p>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-[var(--ink-soft)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="company-search"
              className="w-full rounded-lg border border-[var(--line)] bg-[var(--bone)] py-2 pl-9 pr-3 text-sm outline-none focus:border-[var(--brand)]" />
          </div>
          <div className="space-y-1">
            {filtered.map((c) => (
              <button key={c.id} data-testid={`company-${c.id}`} onClick={() => { setSel(c); setTab("chat"); setShowUsage(false); }}
                className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${sel?.id === c.id ? "bg-[var(--ink)] text-white" : "hover:bg-[var(--bone)]"}`}>
                <span className="min-w-0">
                  <span className="block truncate font-medium">{c.name}</span>
                  <span className={`block truncate text-xs ${sel?.id === c.id ? "text-white/70" : "text-[var(--ink-soft)]"}`}>{c.users} users · {c.library_items} items</span>
                </span>
                {c.unread > 0 && <span className="shrink-0 rounded-full bg-[var(--brand)] px-2 py-0.5 text-[10px] font-bold text-white">{c.unread}</span>}
              </button>
            ))}
          </div>
        </aside>

        <main className={`bg-[var(--bone)] ${(!sel && !showUsage) ? "hidden md:block" : "block"}`}>
          {(sel || showUsage) && (
            <button data-testid="console-back-btn" onClick={() => { setSel(null); setShowUsage(false); }}
              className="flex items-center gap-1.5 px-6 pt-5 text-sm font-semibold text-[var(--ink-soft)] hover:text-[var(--ink)] md:hidden">
              <ChevronLeft className="h-4 w-4" /> All companies
            </button>
          )}
          {showUsage ? (
            <UsagePanel />
          ) : !sel ? (
            <div className="flex h-full min-h-[60vh] flex-col items-center justify-center gap-3 text-[var(--ink-soft)]">
              <Building2 className="h-10 w-10" strokeWidth={1.25} />
              <p className="text-sm">Select a company to message them or manage their library.</p>
            </div>
          ) : (
            <div className="p-6 md:p-8">
              <p className="overline">Platform admin</p>
              <h1 className="font-display text-3xl tracking-tight">{sel.name}</h1>
              <div className="mt-4 flex gap-2">
                <TabBtn active={tab === "chat"} onClick={() => setTab("chat")} icon={MessageSquare} testid="tab-chat">Messages</TabBtn>
                <TabBtn active={tab === "manage"} onClick={() => setTab("manage")} icon={Building2} testid="tab-manage">Account</TabBtn>
                <TabBtn active={tab === "library"} onClick={() => setTab("library")} icon={LibraryBig} testid="tab-library">Their Library</TabBtn>
              </div>
              <div className="mt-6">
                {tab === "chat" ? <AdminChat company={sel} onRead={load} />
                  : tab === "manage" ? <AdminManage company={sel} onChange={load} onDeleted={() => { setSel(null); load(); }} />
                  : <AdminLibrary company={sel} onChange={load} />}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function UsagePanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let on = true;
    setLoading(true);
    api.get(`/admin/usage?days=${days}`)
      .then(({ data }) => { if (on) setData(data); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [days]);

  const t = data?.totals;
  const rates = data?.rates || {};
  return (
    <div className="p-6 md:p-8" data-testid="usage-panel">
      <p className="overline">Platform admin</p>
      <h1 className="font-display text-3xl tracking-tight">Usage &amp; profit</h1>
      <p className="mt-1 max-w-2xl text-sm text-[var(--ink-soft)]">
        Emergent Universal Key AI spend, attributed per company. Credits are estimates — tune the per-operation
        rates in the backend (<code className="rounded bg-[var(--bone)] px-1">CREDITS_PER</code>) after measuring real
        balance drops, and this whole history recomputes.
      </p>

      <div className="mt-4 flex gap-2">
        {[7, 30, 90].map((d) => (
          <button key={d} data-testid={`usage-days-${d}`} onClick={() => setDays(d)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${days === d ? "bg-[var(--ink)] text-white" : "border border-[var(--line)] bg-white hover:bg-[var(--bone)]"}`}>
            Last {d} days
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 flex items-center gap-2 text-[var(--ink-soft)]"><Loader2 className="h-5 w-5 animate-spin" /> Loading usage…</div>
      ) : !data ? null : (
        <>
          <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard label="Est. credits used" value={t.credits} testid="usage-total-credits" />
            <StatCard label="Est. AI cost" value={`$${t.cost_usd.toFixed(2)}`} accent testid="usage-total-cost" icon={DollarSign} />
            <StatCard label="Kitchen renders" value={t.image_gen} testid="usage-total-renders" />
            <StatCard label="Library imports" value={t.vision_analyze} testid="usage-total-imports" />
          </div>

          <p className="mt-6 mb-2 text-xs text-[var(--ink-soft)]">
            Rates: 1 render ≈ {rates.image_gen} cr · 1 import ≈ {rates.vision_analyze} cr · 1 Roger call ≈ {rates.text_llm} cr · $1 = {(1 / data.credit_to_usd).toFixed(0)} credits
          </p>

          <div className="overflow-x-auto rounded-xl border border-[var(--line)] bg-white">
            <table className="w-full text-sm" data-testid="usage-table">
              <thead>
                <tr className="border-b border-[var(--line)] text-left text-xs uppercase tracking-wide text-[var(--ink-soft)]">
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3 text-right">Renders</th>
                  <th className="px-4 py-3 text-right">Imports</th>
                  <th className="px-4 py-3 text-right">Roger</th>
                  <th className="px-4 py-3 text-right">Est. credits</th>
                  <th className="px-4 py-3 text-right">Est. cost</th>
                </tr>
              </thead>
              <tbody>
                {data.companies.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--ink-soft)]">No AI usage recorded in this period yet.</td></tr>
                ) : data.companies.map((c) => (
                  <tr key={c.company_id} data-testid={`usage-row-${c.company_id}`} className="border-b border-[var(--line)] last:border-0 hover:bg-[var(--bone)]">
                    <td className="px-4 py-3 font-medium">{c.name}</td>
                    <td className="px-4 py-3 text-right">{c.image_gen}</td>
                    <td className="px-4 py-3 text-right">{c.vision_analyze}</td>
                    <td className="px-4 py-3 text-right">{c.text_llm}</td>
                    <td className="px-4 py-3 text-right">{c.credits}</td>
                    <td className="px-4 py-3 text-right font-semibold text-[var(--brand)]">${c.cost_usd.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const StatCard = ({ label, value, accent, testid, icon: Icon }) => (
  <div data-testid={testid} className={`rounded-xl border p-4 ${accent ? "border-[var(--brand)]/30 bg-[var(--brand)]/[0.05]" : "border-[var(--line)] bg-white"}`}>
    <p className="flex items-center gap-1 text-xs text-[var(--ink-soft)]">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</p>
    <p className={`mt-1 font-display text-2xl ${accent ? "text-[var(--brand)]" : ""}`}>{value}</p>
  </div>
);

const TabBtn = ({ active, onClick, icon: Icon, children, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${active ? "bg-[var(--ink)] text-white" : "border border-[var(--line)] bg-white hover:bg-[var(--bone)]"}`}>
    <Icon className="h-4 w-4" strokeWidth={1.5} /> {children}
  </button>
);

function AdminChat({ company, onRead }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef();
  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/messages?company_id=${company.id}`);
    setMessages(data); onRead?.();
  }, [company.id, onRead]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post("/admin/messages", { company_id: company.id, text });
      setMessages((m) => [...m, data]); setText("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="flex flex-col" style={{ height: "60vh" }}>
      <div className="thin-scroll flex-1 space-y-3 overflow-y-auto rounded-xl border border-[var(--line)] bg-white p-4">
        {messages.length === 0 && <p className="py-10 text-center text-sm text-[var(--ink-soft)]">No messages yet.</p>}
        {messages.map((m) => {
          const admin = m.sender_role === "admin";
          return (
            <div key={m.id} className={`flex ${admin ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${admin ? "bg-[var(--brand)] text-white" : "bg-[var(--surface-2)]"}`}>
                {!admin && <p className="mb-0.5 text-[11px] font-semibold opacity-70">{m.sender_name}</p>}
                <p className="whitespace-pre-wrap">{m.text}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="mt-3 flex items-center gap-2">
        <input data-testid="admin-message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Reply to this company…"
          className="flex-1 rounded-full border border-[var(--line)] bg-white px-4 py-3 text-sm outline-none focus:border-[var(--brand)]" />
        <button type="submit" data-testid="admin-send-btn" className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--brand)] text-white hover:bg-[var(--brand-dark)]"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
}

function AdminLibrary({ company, onChange }) {
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("cabinet_design");
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [hex, setHex] = useState("#8a6d4a");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [newCatKind, setNewCatKind] = useState("color");
  const fileRef = useRef();
  const importRef = useRef();
  const cat = cats.find((c) => c.key === active) || cats[0];
  const cid = company.id;

  const loadCats = useCallback(async () => {
    const { data } = await api.get(`/categories?as_company=${cid}`);
    setCats(data);
    return data;
  }, [cid]);
  useEffect(() => { loadCats().catch((e) => toast.error(apiError(e.response?.data?.detail) || "Could not load categories")); }, [loadCats]);
  const loadItems = useCallback(async () => {
    const { data } = await api.get(`/options?category=${active}&as_company=${cid}`); setItems(data);
  }, [active, cid]);
  useEffect(() => { loadItems(); setName(""); setFile(null); if (fileRef.current) fileRef.current.value = ""; }, [loadItems]);

  const add = async () => {
    if (!name.trim()) return toast.error("Name required");
    if (cat.kind === "image" && !file) return toast.error("Upload an image");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("category", active); fd.append("name", name.trim()); fd.append("as_company", cid);
      if (cat.kind === "color") fd.append("hex", hex);
      if (file) fd.append("file", file);
      await api.post("/options", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Added to their library");
      setName(""); setFile(null); if (fileRef.current) fileRef.current.value = "";
      loadItems(); onChange?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/options/${id}?as_company=${cid}`);
    setItems((p) => p.filter((i) => i.id !== id)); onChange?.();
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      const { data } = await api.post(`/categories?as_company=${cid}`, { label: newCat.trim(), kind: newCatKind });
      setNewCat("");
      await loadCats();
      setActive(data.key);
      toast.success("Category added for this company");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const bulkImport = async (f) => {
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("category", active); fd.append("file", f); fd.append("as_company", cid);
      const { data } = await api.post("/import-swatches", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Added ${data.count} item${data.count === 1 ? "" : "s"} from photo`);
      loadItems(); onChange?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not read that photo."); }
    finally { setImporting(false); if (importRef.current) importRef.current.value = ""; }
  };

  if (!cat) return <p className="text-sm text-[var(--ink-soft)]">Loading…</p>;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr]">
      <div className="thin-scroll space-y-1 md:max-h-[60vh] md:overflow-y-auto">
        {cats.map((c) => (
          <button key={c.key} data-testid={`console-cat-${c.key}`} onClick={() => setActive(c.key)}
            className={`w-full rounded-md px-3 py-2 text-left text-sm font-medium transition-colors ${active === c.key ? "bg-[var(--ink)] text-white" : "text-[var(--ink-soft)] hover:bg-white"}`}>
            {c.label}
          </button>
        ))}
        <div className="mt-3 rounded-lg border border-dashed border-[var(--line)] p-2">
          <input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category" data-testid="console-new-cat-name"
            className="mb-2 w-full rounded-md border border-[var(--line)] bg-white px-2 py-1.5 text-xs outline-none focus:border-[var(--brand)]" />
          <div className="flex gap-1.5">
            <select value={newCatKind} onChange={(e) => setNewCatKind(e.target.value)} data-testid="console-new-cat-kind"
              className="rounded-md border border-[var(--line)] bg-white px-1.5 py-1.5 text-[11px] outline-none">
              <option value="color">Color</option><option value="image">Image</option>
            </select>
            <button data-testid="console-add-cat" onClick={addCategory} className="flex-1 rounded-full bg-[var(--ink)] py-1.5 text-[11px] font-semibold text-white">Add</button>
          </div>
        </div>
      </div>
      <div>
        <div className="mb-3 flex justify-end">
          <button data-testid="console-import-btn" onClick={() => importRef.current?.click()} disabled={importing}
            className="flex items-center gap-2 rounded-full border border-[var(--brand)] bg-white px-4 py-2 text-xs font-semibold text-[var(--brand)] disabled:opacity-60">
            {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Import from photo
          </button>
          <input ref={importRef} type="file" accept="image/*" className="hidden" data-testid="console-import-input" onChange={(e) => bulkImport(e.target.files?.[0])} />
        </div>
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-[var(--line)] bg-white p-4">
          <input data-testid="console-opt-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name"
            className="flex-1 rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)]" />
          {cat.kind === "color" && <input type="color" value={hex} onChange={(e) => setHex(e.target.value)} className="h-10 w-14 rounded border border-[var(--line)]" data-testid="console-opt-color" />}
          <input ref={fileRef} type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0])} data-testid="console-opt-file"
            className="text-sm text-[var(--ink-soft)] file:mr-2 file:rounded-full file:border-0 file:bg-[var(--surface-2)] file:px-3 file:py-1.5 file:text-xs" />
          <button data-testid="console-add-opt" onClick={add} disabled={saving}
            className="flex items-center gap-1.5 rounded-full bg-[var(--brand)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[var(--brand-dark)] disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
          </button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
          {items.map((o) => (
            <div key={o.id} className="group overflow-hidden rounded-lg border border-[var(--line)] bg-white">
              <div className="relative aspect-square">
                {o.image_url ? <img src={mediaUrl(o.image_url)} alt={o.name} className="h-full w-full object-cover" /> : <div className="h-full w-full" style={{ backgroundColor: o.hex }} />}
                <button data-testid={`console-del-${o.id}`} onClick={() => remove(o.id)} className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-[var(--brand)] opacity-0 group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              <p className="truncate px-2 py-1.5 text-xs font-medium">{o.name}</p>
            </div>
          ))}
          {items.length === 0 && <p className="col-span-full text-sm text-[var(--ink-soft)]">Empty — upload items for this company.</p>}
        </div>
      </div>
    </div>
  );
}


const ROLES = [["owner", "Owner"], ["manager", "Manager"], ["sales_rep", "Sales Rep"]];

function AdminManage({ company, onChange, onDeleted }) {
  const [data, setData] = useState(null);
  const [name, setName] = useState(company.name);
  const [active, setActive] = useState(!!company.subscription_active);
  const [limit, setLimit] = useState(company.per_seat_daily_limit || 20);
  const [savingInfo, setSavingInfo] = useState(false);
  const [creditAmt, setCreditAmt] = useState("");
  const [creditReason, setCreditReason] = useState("");
  const [addingCredits, setAddingCredits] = useState(false);
  const [balance, setBalance] = useState(company.credit_balance || 0);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/companies/${company.id}`);
    setData(data);
    setName(data.company.name);
    setActive(!!data.company.subscription_active);
    setLimit(data.company.per_seat_daily_limit || 20);
    setBalance(data.company.credit_balance || 0);
  }, [company.id]);
  useEffect(() => { load().catch((e) => toast.error(apiError(e.response?.data?.detail))); }, [load]);

  const saveInfo = async () => {
    setSavingInfo(true);
    try {
      await api.patch(`/admin/companies/${company.id}`, { name, subscription_active: active, per_seat_daily_limit: Number(limit) });
      toast.success("Account updated"); onChange?.(); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSavingInfo(false);
  };

  const addCredits = async () => {
    const amt = parseInt(creditAmt, 10);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount of credits to add.");
    setAddingCredits(true);
    try {
      const { data } = await api.post(`/admin/companies/${company.id}/credits`, { amount: amt, reason: creditReason });
      setBalance(data.credit_balance); setCreditAmt(""); setCreditReason("");
      toast.success(`Added ${amt} credits`); onChange?.(); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setAddingCredits(false);
  };

  const updateUser = async (uid, patch) => {
    try { await api.patch(`/admin/users/${uid}`, patch); toast.success("Member updated"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const removeUser = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email} from ${company.name}? This deletes their login.`)) return;
    try { await api.delete(`/admin/users/${m.id}`); toast.success("Member removed"); load(); onChange?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const deleteCompany = async () => {
    if (!window.confirm(`PERMANENTLY delete ${company.name} and ALL its users, library and data? This cannot be undone.`)) return;
    if (!window.confirm("Are you absolutely sure? Type-check: this wipes everything for this account.")) return;
    try { await api.delete(`/admin/companies/${company.id}`); toast.success("Account deleted"); onDeleted?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-manage-panel">
      {/* Account info */}
      <section className="rounded-xl border border-[var(--line)] bg-white p-5">
        <p className="font-semibold">Account info</p>
        <div className="mt-4 space-y-4">
          <div>
            <label className="text-xs font-medium text-[var(--ink-soft)]">Company name</label>
            <input data-testid="manage-name" value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)]" />
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex cursor-pointer items-center gap-2 text-sm" data-testid="manage-active">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="h-4 w-4 accent-[var(--brand)]" />
              Subscription active
            </label>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[var(--ink-soft)]">Daily renders / user</span>
              <input data-testid="manage-limit" type="number" min="1" max="1000" value={limit} onChange={(e) => setLimit(e.target.value)}
                className="w-20 rounded-lg border border-[var(--line)] bg-[var(--bone)] px-2 py-1.5 text-sm outline-none focus:border-[var(--brand)]" />
            </div>
          </div>
          <button data-testid="manage-save-info" onClick={saveInfo} disabled={savingInfo}
            className="flex items-center gap-2 rounded-full bg-[var(--ink)] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
            {savingInfo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
          </button>
        </div>
      </section>

      {/* Credits (add only) */}
      <section className="rounded-xl border border-[var(--line)] bg-white p-5">
        <div className="flex items-center justify-between">
          <p className="font-semibold flex items-center gap-2"><CreditCard className="h-4 w-4 text-[var(--brand)]" /> Credits</p>
          <span className="text-sm">Balance: <b data-testid="manage-balance">{balance}</b></span>
        </div>
        <p className="mt-1 text-xs text-[var(--ink-soft)]">Credits can only be added — they can't be removed.</p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <input data-testid="manage-credit-amount" type="number" min="1" value={creditAmt} onChange={(e) => setCreditAmt(e.target.value)} placeholder="Amount to add"
            className="w-full rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)] sm:w-40" />
          <input data-testid="manage-credit-reason" value={creditReason} onChange={(e) => setCreditReason(e.target.value)} placeholder="Reason (optional, logged)"
            className="flex-1 rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)]" />
          <button data-testid="manage-add-credits" onClick={addCredits} disabled={addingCredits}
            className="flex items-center justify-center gap-2 rounded-full bg-[var(--brand)] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[var(--brand-dark)] disabled:opacity-60">
            {addingCredits ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
          </button>
        </div>
        {data?.credit_log?.length > 0 && (
          <div className="mt-3 space-y-1 border-t border-[var(--line)] pt-3 text-xs text-[var(--ink-soft)]">
            {data.credit_log.slice(0, 5).map((a, i) => (
              <p key={i}>+{a.amount} · {a.reason || "no reason"} · {a.by} · {a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}</p>
            ))}
          </div>
        )}
      </section>

      {/* Team */}
      <section className="rounded-xl border border-[var(--line)] bg-white p-5">
        <p className="font-semibold flex items-center gap-2"><UserCog className="h-4 w-4 text-[var(--brand)]" /> Team ({data?.members?.length || 0})</p>
        <div className="mt-3 space-y-2">
          {data?.members?.map((m) => (
            <div key={m.id} data-testid={`manage-member-${m.id}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-[var(--line)] p-3">
              <input defaultValue={m.name || ""} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== m.name) updateUser(m.id, { name: e.target.value.trim() }); }}
                data-testid={`manage-member-name-${m.id}`}
                className="w-40 rounded-md border border-[var(--line)] bg-[var(--bone)] px-2 py-1.5 text-sm outline-none focus:border-[var(--brand)]" />
              <span className="text-xs text-[var(--ink-soft)]">{m.email}</span>
              <select value={m.role} onChange={(e) => updateUser(m.id, { role: e.target.value })} data-testid={`manage-member-role-${m.id}`}
                className="ml-auto rounded-md border border-[var(--line)] bg-white px-2 py-1.5 text-xs outline-none">
                {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <button data-testid={`manage-member-remove-${m.id}`} onClick={() => removeUser(m)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {(!data || data.members?.length === 0) && <p className="text-sm text-[var(--ink-soft)]">No members.</p>}
        </div>
      </section>

      {/* Danger zone */}
      <section className="rounded-xl border border-red-200 bg-red-50/50 p-5">
        <p className="font-semibold flex items-center gap-2 text-red-700"><ShieldAlert className="h-4 w-4" /> Danger zone</p>
        <p className="mt-1 text-xs text-[var(--ink-soft)]">Permanently delete this account and everything in it. This cannot be undone.</p>
        <button data-testid="manage-delete-company" onClick={deleteCompany}
          className="mt-3 flex items-center gap-2 rounded-full border border-red-300 bg-white px-5 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-600 hover:text-white">
          <Trash2 className="h-4 w-4" /> Delete this account
        </button>
      </section>
    </div>
  );
}
